import { HttpClient } from '@angular/common/http';
import { inject } from '@angular/core';
import { map, Observable } from 'rxjs';
import { JsonApiResource } from '@irian-ro/abstract-resource';
import { EnvironmentService } from '@irian-ro/util-environment';
import { IpEnvironment } from '@ipensii/shared/domain-shared';
import { FilterQueryParams, IndexedMap, MapUtils } from '@ipensii/shared/util-shared';
import { TableLazyLoadEvent } from 'primeng/table';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
export interface JsonApiResourceEmptyMetadata {}

export interface JsonApiListResourceMetadataBase {
  totalSize: number;
}

export abstract class AbstractJsonApiService<
  D,
  M = JsonApiResourceEmptyMetadata,
  MM = JsonApiListResourceMetadataBase
> {
  protected readonly http: HttpClient = inject(HttpClient);
  protected readonly url: string;

  protected constructor(urlPath: string) {
    const env: EnvironmentService<IpEnvironment> = inject(EnvironmentService);
    this.url = `${env.getEnvironment().apiUrl}/${urlPath}`;
  }

  findAllJsonApi(): Observable<JsonApiResource<Array<D>, MM>> {
    return this.http.get<JsonApiResource<Array<D>, MM>>(this.url);
  }

  findAll(): Observable<Array<D>> {
    return this.findAllJsonApi().pipe(map((res) => res.data));
  }

  findOneJsonApi({ id }: { id: string } = { id: '' }): Observable<JsonApiResource<D, M>> {
    return this.http.get<JsonApiResource<D, M>>(id ? `${this.url}/${id}` : this.url);
  }

  findOne({ id }: { id: string } = { id: '' }): Observable<D> {
    return this.findOneJsonApi({ id }).pipe(map((res) => res.data));
  }

  findWithFilterJsonApi(lazyLoadEvent?: TableLazyLoadEvent): Observable<JsonApiResource<Array<D>, M>> {
    const url = new FilterQueryParams(lazyLoadEvent).toString();
    return this.http.get<JsonApiResource<Array<D>, M>>(`${this.url}${url}`);
  }

  findWithFilter(lazyLoadEvent?: TableLazyLoadEvent): Observable<Array<D>> {
    return this.findWithFilterJsonApi(lazyLoadEvent).pipe(map((res) => res.data));
  }

  findJsonApi(query?: IndexedMap<string, unknown>): Observable<JsonApiResource<D, M>> {
    const url = query ? MapUtils.keyValues(query).reduce((acc, [k, v]) => `${acc}/${k}/${v}`, this.url) : this.url;
    return this.http.get<JsonApiResource<D, M>>(url);
  }

  find(query?: IndexedMap<string, unknown>): Observable<D> {
    return this.findJsonApi(query).pipe(map((res) => res.data));
  }

  saveJsonApi<T = D>(resource: T): Observable<JsonApiResource<D, M>> {
    return this.http.post<JsonApiResource<D, M>>(`${this.url}`, resource);
  }

  save<T = D>({ resource }: { resource: T }): Observable<D> {
    return this.saveJsonApi(resource).pipe(map((res) => res.data));
  }

  getJsonApi<T = D, MT = M>(path?: string): Observable<JsonApiResource<T, MT>> {
    return this.http.get<JsonApiResource<T, MT>>(path ? `${this.url}/${path}` : this.url);
  }

  get<T = D>(path?: string): Observable<T> {
    return this.getJsonApi<T>(path).pipe(map((res) => res.data));
  }

  delete({ id }: { id: string } = { id: '' }): Observable<JsonApiResource<D, M>> {
    return this.http.delete<JsonApiResource<D, M>>(`${this.url}/${id}`);
  }

  postJsonApi<T = D, MT = M, B = D>(path?: string, body?: B): Observable<JsonApiResource<T, MT>> {
    return this.http.post<JsonApiResource<T, MT>>(path ? `${this.url}/${path}` : this.url, body);
  }

  post<T = D, MT = M, B = D>(path?: string, body?: B): Observable<T> {
    return this.postJsonApi<T, MT, B>(path, body).pipe(map((res) => res && res.data));
  }

  putJsonApi<T = D, MT = M, B = D>(path?: string, body?: B): Observable<JsonApiResource<T, MT>> {
    return this.http.put<JsonApiResource<T, MT>>(path ? `${this.url}/${path}` : this.url, body);
  }

  put<T = D, MT = M, B = D>(path?: string, body?: B): Observable<T> {
    return this.putJsonApi<T, MT, B>(path, body).pipe(map((res) => res.data));
  }
}
